import React, { useState, useEffect, useMemo } from "react";
import { FaGithub, FaFireAlt } from "react-icons/fa";

const GitHubContributionCalendar = () => {
  const username = "praveenkumar-king";
  const [selectedYear, setSelectedYear] = useState("2026");
  const [availableYears, setAvailableYears] = useState(["2026", "2025", "2024"]);
  const [contributionsData, setContributionsData] = useState(null);
  const [totalCounts, setTotalCounts] = useState({ "2026": 50, "2025": 24, "2024": 6 });
  const [hoveredDay, setHoveredDay] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchContributions = async () => {
      try {
        const res = await fetch(`https://github-contributions-api.jogruber.de/v4/${username}`);
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();

        if (isMounted && data && data.contributions) {
          setContributionsData(data.contributions);
          if (data.total) {
            setTotalCounts(data.total);
            const years = Object.keys(data.total).sort((a, b) => b - a);
            if (years.length > 0) {
              setAvailableYears(years);
              if (!years.includes(selectedYear)) {
                setSelectedYear(years[0]);
              }
            }
          }
        }
      } catch (err) {
        console.warn("Could not fetch live contribution API, using fallback data.");
        if (isMounted && !contributionsData) {
          setError(false); // Graceful fallback will generate proper placeholder grid
        }
      }
    };

    fetchContributions();

    return () => {
      isMounted = false;
    };
  }, [username, selectedYear, contributionsData]);

  // Build weeks grid for selected year
  const { weeks, monthLabels, currentYearTotal } = useMemo(() => {
    let days = [];

    if (contributionsData && contributionsData.length > 0) {
      days = contributionsData.filter((item) => item.date.startsWith(selectedYear));
    }

    // Fallback generation if API fails or while initial data is arriving
    if (!days || days.length === 0) {
      const isLeap = (parseInt(selectedYear, 10) % 4 === 0 && parseInt(selectedYear, 10) % 100 !== 0) || parseInt(selectedYear, 10) % 400 === 0;
      const totalDays = isLeap ? 366 : 365;
      const startDate = new Date(`${selectedYear}-01-01T00:00:00Z`);

      for (let i = 0; i < totalDays; i++) {
        const d = new Date(startDate);
        d.setUTCDate(d.getUTCDate() + i);
        const dateStr = d.toISOString().split("T")[0];
        days.push({ date: dateStr, count: 0, level: 0 });
      }
    }

    // Group into 7-day weeks (Sunday = index 0, Saturday = index 6)
    const weeksList = [];
    let currentWeek = [];

    if (days.length > 0) {
      const firstDayOfWeek = new Date(`${days[0].date}T00:00:00`).getDay();
      for (let i = 0; i < firstDayOfWeek; i++) {
        currentWeek.push(null);
      }

      days.forEach((day) => {
        currentWeek.push(day);
        if (currentWeek.length === 7) {
          weeksList.push(currentWeek);
          currentWeek = [];
        }
      });

      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null);
        }
        weeksList.push(currentWeek);
      }
    }

    // Determine month label positions
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const labels = [];
    let lastMonth = -1;

    weeksList.forEach((week, weekIdx) => {
      // Look for the first non-null day in this week
      const validDay = week.find((d) => d !== null);
      if (validDay) {
        const monthIndex = new Date(`${validDay.date}T00:00:00`).getMonth();
        if (monthIndex !== lastMonth) {
          labels.push({ month: monthNames[monthIndex], weekIndex: weekIdx });
          lastMonth = monthIndex;
        }
      }
    });

    const yearTotal = totalCounts[selectedYear] || days.reduce((sum, d) => sum + (d.count || 0), 0);

    return {
      weeks: weeksList,
      monthLabels: labels,
      currentYearTotal: yearTotal
    };
  }, [contributionsData, selectedYear, totalCounts]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const options = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", options);
  };

  return (
    <div className="activity-calendar-wrapper" data-aos="fade-up">
      {/* Calendar Header */}
      <div className="calendar-header-row">
        <div className="calendar-title-group">
          <span className="calendar-icon">
            <FaGithub />
          </span>
          <div>
            <h2 className="calendar-title">GitHub Contribution Activity</h2>
            <p className="calendar-subtitle">
              <FaFireAlt className="fire-icon" /> <strong>{currentYearTotal}</strong> contributions in {selectedYear}
            </p>
          </div>
        </div>

        {/* Year Selectors */}
        <div className="year-selector-tabs" role="tablist">
          {availableYears.map((yr) => (
            <button
              key={yr}
              type="button"
              role="tab"
              aria-selected={selectedYear === yr}
              className={`year-tab ${selectedYear === yr ? "active" : ""}`}
              onClick={() => setSelectedYear(yr)}
            >
              {yr}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="calendar-fallback-state">
          <p>GitHub activity is currently unavailable.</p>
          <a
            href={`https://github.com/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="calendar-fallback-link"
          >
            View profile directly on GitHub →
          </a>
        </div>
      ) : (
        <div className="heatmap-scroll-container">
          <div className="heatmap-inner-content">
            {/* Months Header */}
            <div className="heatmap-months-row">
              <div className="day-label-spacer" />
              <div className="heatmap-weeks-labels">
                {weeks.map((_, idx) => {
                  const label = monthLabels.find((m) => m.weekIndex === idx);
                  return (
                    <span key={idx} className="month-label">
                      {label ? label.month : ""}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Grid with Day Labels */}
            <div className="heatmap-body-row">
              {/* Day of Week Labels */}
              <div className="day-labels-col">
                <span className="day-label"></span>
                <span className="day-label">Mon</span>
                <span className="day-label"></span>
                <span className="day-label">Wed</span>
                <span className="day-label"></span>
                <span className="day-label">Fri</span>
                <span className="day-label"></span>
              </div>

              {/* Heatmap Grid */}
              <div className="heatmap-grid" role="grid" aria-label="GitHub contribution calendar">
                {weeks.map((week, wIdx) => (
                  <div key={wIdx} className="heatmap-week-col" role="row">
                    {week.map((day, dIdx) => {
                      if (!day) {
                        return <div key={dIdx} className="heatmap-cell empty" />;
                      }

                      const tooltip = day.count > 0
                        ? `${day.count} contribution${day.count > 1 ? "s" : ""} on ${formatDate(day.date)}`
                        : `No contributions on ${formatDate(day.date)}`;

                      return (
                        <div
                          key={dIdx}
                          role="gridcell"
                          tabIndex={0}
                          className={`heatmap-cell level-${day.level || 0}`}
                          aria-label={tooltip}
                          onMouseEnter={() => setHoveredDay(tooltip)}
                          onMouseLeave={() => setHoveredDay(null)}
                          onFocus={() => setHoveredDay(tooltip)}
                          onBlur={() => setHoveredDay(null)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar Footer: Live Tooltip & Legend */}
            <div className="calendar-footer-row">
              <div className="hover-tooltip-display">
                {hoveredDay ? (
                  <span className="tooltip-active-text">{hoveredDay}</span>
                ) : (
                  <span className="tooltip-hint-text">Hover or tap on any square to view details</span>
                )}
              </div>

              <div className="calendar-legend">
                <span className="legend-label">Less</span>
                <div className="legend-cells">
                  <div className="heatmap-cell level-0" title="0 contributions" />
                  <div className="heatmap-cell level-1" title="1-3 contributions" />
                  <div className="heatmap-cell level-2" title="4-6 contributions" />
                  <div className="heatmap-cell level-3" title="7-9 contributions" />
                  <div className="heatmap-cell level-4" title="10+ contributions" />
                </div>
                <span className="legend-label">More</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubContributionCalendar;
